import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import _ from 'underscore';
import { keyCode } from '@bingads-webui/keycode';
import { WithArrowKeys } from '@bingads-webui-react/hoc-with-arrow-keys';
import { ListItem, ListItemWithToolTip } from './list-item';
import { AriaRoleType, ItemAriaRoleConfig } from './aria-role-config';

/**
 * A list component contains a collection of items,
 */

class RawListSelector extends PureComponent {
  static defaultProps = {
    renderHeader: undefined,
    renderFooter: undefined,
    renderDivider: undefined,
    renderEmptyList: undefined,
    renderActionBar: undefined,
    keyExtractor: (item, index) => item.Id || index,
    typeExtractor: item => item.type,
    extraData: undefined,
    onSelect: _.noop,
    selectedItem: undefined,
    checkSelected: (selectedItem, item) => _.isEqual(selectedItem, item),
    enableArrowKeysSupport: false,
    observable: _.noop,
    onKeyDown: _.noop,
    onBlur: _.noop,
    ariaLabel: '',
    ariaRole: AriaRoleType.ListBox,
    enableOverflowTooltip: false,
  };

  constructor(props) {
    super(props);

    const {
      enableArrowKeysSupport,
      observable,
    } = props;

    let observer = { next: _.noop };
    if (enableArrowKeysSupport) {
      observer = {
        next: (data) => {
          switch (data) {
            case keyCode.ArrowUp:
              this.setState((preState, prevProps) => ({
                focusedIndex: this.getNextFocusedItemIndex(preState.focusedIndex, prevProps, false),
              }));
              break;
            case keyCode.ArrowDown:
              this.setState((preState, prevProps) => ({
                focusedIndex: this.getNextFocusedItemIndex(preState.focusedIndex, prevProps, true),
              }));
              break;
            case keyCode.Tab:
              this.setState({ focusedIndex: -1 });
              break;
            default:
              break;
          }
        },
      };
    }

    observable.subscribe(observer);

    this.state = { focusedIndex: -1 };
  }

  onBlur = () => {
    this.setState({ focusedIndex: -1 });
    this.props.onBlur();
  }

  getNextFocusedItemIndex = (previousIndex, props, isNext) => {
    const { data, typeExtractor } = props;
    const maxIndex = data.length - 1;

    let newIndex;

    if (previousIndex < 0) {
      newIndex = 0;
    } else if (previousIndex > maxIndex) {
      newIndex = maxIndex;
    } else if (isNext && previousIndex === maxIndex) {
      newIndex = 0;
    } else if (!isNext && previousIndex === 0) {
      newIndex = maxIndex;
    } else {
      newIndex = isNext ? previousIndex + 1 : previousIndex - 1;
    }

    if (typeExtractor(data[newIndex]) === 'divider') {
      return this.getNextFocusedItemIndex(newIndex, props, isNext);
    }

    return newIndex;
  }

  focusItemCallback = (index) => {
    this.setState({ focusedIndex: index });
  }

  render() {
    const {
      renderHeader,
      renderFooter,
      renderEmptyList,
      renderItem,
      renderActionBar,
      keyExtractor,
      renderDivider,
      ariaLabel,
      ariaRole,
      data,
      enableOverflowTooltip,
      onKeyDown,
    } = this.props;

    const ListItemClass = enableOverflowTooltip ? ListItemWithToolTip : ListItem;
    const validItemsCount = _.size(_.filter(this.props.data, item => this.props.typeExtractor(item) !== 'divider'));
    let positionInValidItems = 0;

    // this false alarm rule requires a role on div but doesn't recognize dynamic role
    /* eslint-disable jsx-a11y/no-static-element-interactions */
    return (
      <div className="ba-list-selector">
        {renderHeader && (
          <div className="list-selector-header">{renderHeader()}</div>
        )}

        {data.length > 0 && renderActionBar && (
          <div className="list-selector-action-bar" role="menu">{renderActionBar({ itemCount: data.length })}</div>
        )}

        <div
          className="list-selector-content"
          role={ariaRole}
          onKeyDown={event => onKeyDown(event)}
          onBlur={this.onBlur}
          aria-label={ariaLabel || 'selected result'}
        >
          {_.map(this.props.data, (item, index) => {
            const type = this.props.typeExtractor(item);
            const key = keyExtractor(item, index);

            switch (type) {
              case 'divider':
                return renderDivider ? (
                  renderDivider(item, index, key)
                ) : (
                  <hr key={key} />
                  );
              default:
                positionInValidItems += 1;
                return (
                  <ListItemClass
                    ariaRole={ItemAriaRoleConfig[ariaRole]}
                    listSize={validItemsCount}
                    positionInList={positionInValidItems}
                    item={item}
                    index={index}
                    key={key}
                    keyValue={key}
                    onSelect={this.props.onSelect}
                    checkSelected={this.props.checkSelected}
                    selectedItem={this.props.selectedItem}
                    renderItem={renderItem}
                    isFocused={index === this.state.focusedIndex}
                    focusItemCallback={this.focusItemCallback}
                  />
                );
            }
          })}

          {this.props.data.length === 0 && renderEmptyList && (
            <div className="list-selector-empty">{renderEmptyList()}</div>
          )}
        </div>
        {renderFooter && (
          <div className="list-selector-footer">{renderFooter()}</div>
        )}
      </div>
    );
  }
}

RawListSelector.propTypes = {
  /**
   * Rendered at the top of all the items. A render function that returns a React element.
   *
   * @return {ReactElement} Rendered Header element
   */
  renderHeader: PropTypes.func,
  /**
   * Rendered at the top of all the items. A render function that returns a React element.
   *
   * @return {ReactElement} Rendered Footer element
   */
  renderFooter: PropTypes.func,
  /**
   * Takes an item from `data` and renders it into the list,
   * provides additional metadata like `index` if you need it.
   *
   * @param {Object} item - item among data
   * @param {number} index - item index
   * @return {ReactElement} Rendered item element
   */
  renderItem: PropTypes.func.isRequired,
  /**
   * Takes an item from `data` and renders it into the list as item divider,
   * provides additional metadata like `index` if you need it.
   *
   * @param {Object} item - item among data
   * @param {number} index - item index
   * @return {ReactElement} Rendered item element
   */
  renderDivider: PropTypes.func,
  /**
   * Rendered action bar at the top of all the items, like select all or remove all
   * A render function that returns a React element.
   * while omitted, will not render
   *
   * @return {ReactElement} Rendered item element
   */
  renderActionBar: PropTypes.func,
  /**
   * For simplicity, data is just a plain array.
   * Support divider as build-in item, divider should be declared as with type:'divider'.
   */
  data: PropTypes.arrayOf(PropTypes.any).isRequired,
  /**
   * Fired when an item in the list is selected
   * @param {object} item - selected item
   * @param {number} index - the index of selected item in the list
   * @return {void}
   */
  onSelect: PropTypes.func,
  /**
   * Rendered when the list is empty. A render function that returns a React element.
   *
   * @return {ReactElement} Rendered empty list element
   */
  renderEmptyList: PropTypes.func,
  /**
   * Selected item in the list
   */
  selectedItem: PropTypes.objectOf(PropTypes.any),
  /**
   * A marker property for telling the list to re-render (since it implements `PureComponent`). If
   * any of your render functions depend on anything outside of the
   * `data` prop, stick it here.
   */
  extraData: PropTypes.any, //eslint-disable-line
  /**
   * Used to extract a unique key for a given item at the specified index. Key is used
   * as the react key to track item re-ordering. The default extractor checks `item.key`, then
   * falls back to using the index, like React does.
   *
   * @param {Object} item - single data in the list
   * @param {number} index - item index among data
   * @return {number|string} - unique key of the item
   */
  keyExtractor: PropTypes.func,
  /**
   * Used to extract type for a given item.
   * Type: undefined - render as default selector item,
   *       will trigger onSelect function when clicked.
   * Type: divider - render as divier in selector list,
   *       will not trigger onSelect function when clicked.
   *
   * @param {Object} item - single data in the list
   * @return {string} - type of the item
   */
  typeExtractor: PropTypes.func,
  /**
   * Used to check whether an item is selected.
   *
   * @param {Object} selectedItem - selected item
   * @param {Object} item - single data in the list
   * @return {boolean} - whether current item is selected
   */
  checkSelected: PropTypes.func,
  /**
   * Used to check if arrow keys are supported to manage focus state.
   *
   */
  enableArrowKeysSupport: PropTypes.bool,
  /**
   * Used to subscribe a observer for arrow keys event.
   *
   */
  observable: PropTypes.shape({
    subscribe: PropTypes.func,
  }),
  /**
   * Used to handle keydown event
   *
   */
  onKeyDown: PropTypes.func,
  /**
   * Used to handle blur event
   *
   */
  onBlur: PropTypes.func,
  /**
   * Used to specify aria label for the list box.
   *
   */
  ariaLabel: PropTypes.string,
  /**
   * Used to specify aria role for the list box. Default is listbox.
   *
   */
  ariaRole: PropTypes.string,
  /**
   * Used to enable the overflow tooltip for list item.
   *
   */
  enableOverflowTooltip: PropTypes.bool,
};

export const ListSelector = WithArrowKeys(RawListSelector, { interceptTabKey: true });
